import { create } from 'zustand'
import type {
  IPropertyDetail,
  IPortalLightCredential
} from '../../../interfaces'

export interface ModalState {
  isOpen: boolean
  type: 'publish' | 'unpublish'
  propertiesDetail: IPropertyDetail[]
  propertiesIds: string[]
  onSuccess?: (data: any) => void
}

interface ModalPublishOrUnpublishPropertyState {
  modalState: ModalState
  selectedPortalsIds: string[]
  selectedAssociations: string[]
  initialAssociations: string[]
  dataAssociations: IPortalLightCredential[]
  selectedAdOptions: {
    portalId: string
    adOptionId: string
    name: string
    available?: number
  }[]
  availablePortals: IPortalLightCredential[]
  setIsModalState: ({
    isOpen,
    type,
    propertiesDetail,
    propertiesIds,
    onSuccess
  }: ModalState) => void
  setDataAssociations: (dataAssociations: IPortalLightCredential[]) => void
  setInitialAssociations: (initialAssociations: string[]) => void
  setAvailablePortals: (availablePortals: IPortalLightCredential[]) => void
  toggleSelectedPortalId: (portalId: string) => void
  toggleSelectedAssociation: (associationId: string) => void
  portalIsChecked: (portalId: string) => boolean
  toggleSelectAdOption: (
    portalId: string,
    adOptionId: string,
    name: string,
    available?: number
  ) => void
  associationIsChecked: (associationId: string) => boolean
  resetModalState: () => void
}

const modalPublishOrUnpublishInitialState: {
  modalState: ModalState
  availablePortals: IPortalLightCredential[]
  selectedPortalsIds: string[]
  dataAssociations: IPortalLightCredential[]
  selectedAdOptions: {
    portalId: string
    adOptionId: string
    name: string
    available?: number
  }[]
  selectedAssociations: string[]
  initialAssociations: string[]
} = {
  selectedPortalsIds: [],
  selectedAssociations: [],
  availablePortals: [],
  dataAssociations: [],
  selectedAdOptions: [],
  initialAssociations: [],
  modalState: {
    isOpen: false,
    type: 'publish',
    propertiesIds: [],
    propertiesDetail: []
  }
}

export const useModalPublishOrUnpublishPropertyStore =
  create<ModalPublishOrUnpublishPropertyState>()((set, get) => ({
    ...modalPublishOrUnpublishInitialState,
    setDataAssociations: (dataAssociations: IPortalLightCredential[]) =>
      set({ dataAssociations }),
    setInitialAssociations: (initialAssociations: string[]) =>
      set({ initialAssociations }),
    setIsModalState: (modalState: ModalState) => set({ modalState }),
    resetModalState: () => set(modalPublishOrUnpublishInitialState),
    setAvailablePortals: (availablePortals: IPortalLightCredential[]) =>
      set({ availablePortals }),
    toggleSelectedPortalId: (portalId: string) => {
      set((state) => {
        const isSelected = state?.selectedPortalsIds?.includes(portalId)
        return isSelected
          ? {
              selectedPortalsIds: state?.selectedPortalsIds?.filter(
                (selectedPortalId) => selectedPortalId !== portalId
              )
            }
          : {
              selectedPortalsIds: [...state?.selectedPortalsIds, portalId]
            }
      })
    },
    portalIsChecked: (portalId: string) => {
      return get().selectedPortalsIds?.includes(portalId)
    },
    associationIsChecked: (associationId: string) => {
      return get().selectedAssociations?.includes(associationId)
    },
    toggleSelectedAssociation: (associationId: string) => {
      set((state) => {
        const isSelected = state?.selectedAssociations?.includes(associationId)
        return isSelected
          ? {
              selectedAssociations: state?.selectedAssociations?.filter(
                (selectedAssociationId) =>
                  selectedAssociationId !== associationId
              )
            }
          : {
              selectedAssociations: [
                ...state?.selectedAssociations,
                associationId
              ]
            }
      })
    },
    toggleSelectAdOption: (
      portalId: string,
      adOptionId: string,
      name: string,
      available?: number
    ) => {
      set((state) => {
        const portalIdExist = state?.selectedAdOptions?.find(
          (selectedAdOption) => selectedAdOption?.portalId === portalId
        )
        return portalIdExist
          ? {
              selectedAdOptions: state?.selectedAdOptions?.map(
                (selectedAdOption) => {
                  return selectedAdOption?.portalId === portalId
                    ? { portalId, adOptionId, name, available }
                    : selectedAdOption
                }
              )
            }
          : {
              selectedAdOptions: [
                ...state?.selectedAdOptions,
                { portalId, adOptionId, name, available }
              ]
            }
      })
    }
  }))

export default useModalPublishOrUnpublishPropertyStore
